import type { NavigateFunction } from 'react-router-dom'

import { Tag as TagBadge } from '@buffer-mono/popcorn'

import type { Tag } from '~publish/hooks/useTags'

import { PRIORITIES } from '../constants'
import { createAction } from './createAction'
import type { CommandBarAction } from './types'

import styles from './tagActions.module.css'

const TAG_FILTERABLE_PATHS = ['/schedule', '/all-channels']

function supportsTagFiltering(pathname: string): boolean {
  return TAG_FILTERABLE_PATHS.some((path) => pathname.startsWith(path))
}

const COLOR_NAMES: Record<string, string[]> = {
  '#3d7dc9': ['blue'],
  '#4da1ff': ['light blue', 'sky blue', 'cyan'],
  '#8fc258': ['green', 'lime'],
  '#9c6fcc': ['purple', 'violet'],
  '#cc4a67': ['pink', 'magenta', 'rose'],
  '#d17f4e': ['orange', 'amber'],
  '#e8c448': ['yellow', 'gold'],
  '#5c5c5c': ['gray', 'grey'],
  '#1e1e1e': ['black', 'dark'],
  '#e05050': ['red', 'crimson'],
  '#50c878': ['emerald', 'mint'],
  '#f0a0a0': ['salmon', 'coral'],
}

function getColorKeywords(colorHex: string): string[] {
  const normalizedColor = colorHex.toLowerCase()
  return COLOR_NAMES[normalizedColor] ?? []
}

/**
 * Options for building tag-related actions.
 */
interface BuildTagActionsOptions {
  /** List of tags to create actions for */
  tags: Tag[]
  /** React Router navigate function for navigation */
  navigate: NavigateFunction
  /** Current pathname for context-aware filtering */
  currentPathname?: string
  /** Current search string for preserving other query params */
  currentSearch?: string
}

/**
 * Builds command bar actions for all user tags.
 * Each tag navigates to the all-channels view filtered by that tag,
 * or adds the tag filter to the current page if it supports tag filtering.
 *
 * @param options - Configuration options
 * @param options.tags - List of tags to create actions for
 * @param options.navigate - React Router navigate function
 * @param options.currentPathname - Current URL pathname for context-aware filtering
 * @param options.currentSearch - Current URL search params to preserve
 * @returns Array of command bar actions for tags
 */
export function buildTagActions({
  tags,
  navigate,
  currentPathname = '',
  currentSearch = '',
}: BuildTagActionsOptions): CommandBarAction[] {
  const actions: CommandBarAction[] = []

  for (const tag of tags) {
    const colorKeywords = getColorKeywords(tag.color)

    actions.push(
      createAction({
        id: `tag-${tag.id}`,
        name: tag.name,
        keywords: [
          tag.name.toLowerCase(),
          'tag',
          'label',
          'filter',
          ...colorKeywords,
        ],
        icon: (
          <TagBadge color={tag.color} className={styles.colorIndicator}>
            {''}
          </TagBadge>
        ),
        section: 'tags',
        priority: PRIORITIES.settings,
        perform: () => {
          if (supportsTagFiltering(currentPathname)) {
            const params = new URLSearchParams(currentSearch)
            params.delete('tags[]')
            params.append('tags[]', tag.id)
            navigate({ pathname: currentPathname, search: params.toString() })
          } else {
            const searchParams = new URLSearchParams()
            searchParams.append('tags[]', tag.id)
            navigate(`/all-channels?${searchParams.toString()}`)
          }
        },
      }),
    )
  }

  return actions
}
